import type { CountryConfig } from './types.js';
import { clPublic } from './cl.public.js';
import { isLegalEntityRut, isValidRut } from './cl-rut.js';

export {
  CL_LEGAL_ENTITY_MIN_BODY,
  canonicalRut,
  isLegalEntityRut,
  isValidRut,
  rutCheckDigit,
} from './cl-rut.js';

/**
 * The SHAPE of a legal entity's RUT: a body from 50 000 000 up (eight digits,
 * the first 5–9), a dash, the check digit. Plain character classes only, so the
 * same source reads identically as a JavaScript regex and as a Postgres `~`
 * pattern — the company step suggests only ids of this shape
 * (`companyId.suggestPattern`), never a natural person's.
 */
export const CL_LEGAL_ENTITY_RUT_SHAPE = '^[5-9][0-9]{7}-[0-9K]$';

/** A UNSPSC commodity code as Mercado Público prints it: exactly 8 digits. */
const UNSPSC_CODE = /^\d{8}$/;

export const clConfig: CountryConfig = {
  ...clPublic,
  // Mercado Público: the licitaciones and the Compra Ágil purchases, two
  // `TenderSourceAdapter`s written on their own branch. Each needs its
  // `tender_sources` row (`prisma/cl-seed.ts`, seeded DISABLED).
  sourceKeys: ['mp-licitaciones', 'mp-compra-agil'],
  // `tender_results.method` holds Mercado Público's procedure name. A «trato
  // directo» is awarded without a competition. A Compra Ágil asks for quotes
  // from every supplier (at least three are compared), so it stays competitive.
  // A row with no method is not counted as a competition — the ES/KZ call.
  competition: {
    nonCompetitiveSources: [],
    nonCompetitiveMethodMarkers: ['trato directo'],
    unknownMethodIsCompetitive: false,
  },
  companyId: {
    label: 'RUT',
    validate: isValidRut,
    // Suggestions offer legal entities only: a natural person's RUT is never
    // looked up by name or number for somebody else (Ley 19.628 / 21.719).
    suggestPattern: CL_LEGAL_ENTITY_RUT_SHAPE,
    // Names are Spanish: accents folded and the legal form dropped, as on ES.
    nameSearch: 'es',
    // After a known RUT: every award of the company, as on ES.
    payoff: 'es',
  },
  // Mercado Público items carry a UNSPSC code (8 digits) in the feed itself.
  // Keep a source-provided code, otherwise decline — no keyword classifier
  // (the research warns the codes are sometimes wrong, so search stays
  // text-first). Anything that is not 8 digits (blank included) is no code.
  classifier: {
    resolve: (code, _title) => {
      const c = code?.trim() ?? '';
      return UNSPSC_CODE.test(c) ? c : null;
    },
    classify: (_title) => null,
  },
  // ≥2 competitive awards in 36 months, no ACTIVE test, LEGAL ENTITIES ONLY —
  // `indexableId` keeps every natural person's page out of the index.
  companyIndex: {
    minCompetitiveAwards: 2,
    windowMonths: 36,
    requireActiveStatus: false,
    indexableId: isLegalEntityRut,
  },
  // Whole pesos. Floor $ 1.000: a Compra Ágil can be a few thousand pesos, but
  // a smaller amount is a placeholder. Ceiling 50 billones: roughly 10× the
  // largest Chilean public contracts (concesiones of a few billones).
  awardPlausibility: { floor: 1_000, ceiling: 50_000_000_000_000 },
  // As on ES: an existing account always gets the emailed code.
  auth: { codeRequiredForExisting: true },
  // As on ES: Lucía sets up or adjusts the writer's saved search herself.
  support: { assistantManagesSearches: true },
};
